import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import Spinner from '../Spinner';
import type { spinnerStyles } from '../Spinner/Spinner';
import type { IButtonProps } from './IButtonProps';

export const buttonStyles = cva(
  'min-w-[130px] h-12 rounded-xl gap-2 text-sm font-semibold inline-flex items-center justify-center p-[10px] max-w-full',
  {
    variants: {
      intent: {
        primary:
          'text-white bg-mobile-primary-500 focus:bg-mobile-primary-600 hover:bg-mobile-primary-600 disabled:bg-mobile-gray-200',
        secondary:
          'bg-white border-2 border-mobile-gray-100 text-mobile-hero-100',
        tertiary:
          'bg-none text-mobile-primary-500 active:bg-mobile-primary-100 p-0 w-auto',
      },
      size: {
        sm: 'h-[34px]',
        md: 'h-12',
        lg: 'h-14',
      },
      defaultVariants: {
        intent: 'primary',
        size: 'sm',
      },
    },
  }
);

const buttonToSpinnerIntentMap = new Map<
  VariantProps<typeof buttonStyles>['intent'],
  VariantProps<typeof spinnerStyles>['intent']
>([
  ['primary', 'secondary'],
  ['secondary', 'primary'],
  ['tertiary', 'tertiary'],
]);

const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      children,
      className,
      intent = 'primary',
      prefixIcon: PrefixIcon,
      trailingIcon: TrailingIcon,
      loading,
      loadingText = '',
      ...rest
    },
    forwardRef
  ) => {
    return (
      <button
        type="button"
        className={twMerge(
          buttonStyles({ intent }),
          className,
          clsx({
            'disabled:bg-opacity-[0.7] cursor-not-allowed': loading,
            'disabled:bg-mobile-primary-500': loading && intent === 'primary',
            'disabled:bg-white': loading && intent === 'secondary',
            'disabled:bg-none': loading && intent === 'tertiary',
          })
        )}
        ref={forwardRef}
        {...rest}
        disabled={rest.disabled || loading}
      >
        {PrefixIcon && !loading ? (
          <span>
            <PrefixIcon />
          </span>
        ) : null}
        {loading ? (
          <>
            <Spinner
              intent={buttonToSpinnerIntentMap.get(intent ?? 'primary')}
            />
            {loadingText}
          </>
        ) : (
          children
        )}
        {TrailingIcon ? (
          <span>
            <TrailingIcon />
          </span>
        ) : null}
      </button>
    );
  }
);
Button.displayName = 'Button';

export default Button;
